/** Payroll feature constants — dept labels, region map, helpers. */

import { formatVNDShort } from "@/lib/format";
import type { UpsellTier } from "./api";

// Region mapping now lives in the shared lib (also used by the staff filter).
// Re-exported here so existing payroll imports keep working.
export { REGIONS, regionOfBranch, type RegionKey } from "@/lib/region";

export const DEPT_LABELS: Record<string, string> = {
  bod: "Ban giám đốc",
  sale: "Kinh doanh",
  sales: "Kinh doanh",
  marketing: "Marketing",
  reception: "Lễ tân",
  letan: "Lễ tân",
  photo: "Chụp ảnh",
  photo_idphoto: "Chụp ảnh thẻ",
  photo_profile: "Chụp ảnh profile",
  photo_corporate: "Chụp ảnh doanh nghiệp",
  makeup: "Trang điểm",
  retouch: "Hậu kỳ",
  cskh: "CSKH",
  studio_assistant: "Trợ lý studio",
  accounting: "Kế toán",
  office: "Văn phòng",
  driver: "Lái xe",
  food: "Bếp / Food",
};

export const DEPT_ICONS: Record<string, string> = {
  bod: "👑",
  sale: "💰",
  sales: "💰",
  marketing: "📢",
  reception: "🏪",
  letan: "🏪",
  photo: "📷",
  photo_idphoto: "🪪",
  photo_profile: "📸",
  photo_corporate: "🏢",
  makeup: "💄",
  retouch: "🖌️",
  cskh: "📞",
  studio_assistant: "🧰",
  accounting: "🧾",
  office: "🏢",
  driver: "🚗",
  food: "🍳",
};

export function deptLabel(key: string): string {
  return DEPT_LABELS[key] ?? key;
}

export function deptIcon(key: string): string {
  return DEPT_ICONS[key] ?? "👤";
}

/** Previous month string YYYY-MM. */
export function prevMonth(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  let year = Number(m[1]);
  let mon = Number(m[2]) - 1;
  if (mon === 0) {
    year -= 1;
    mon = 12;
  }
  return `${year}-${String(mon).padStart(2, "0")}`;
}

export function vnd(n: number | null | undefined): string {
  if (n == null) return "—";
  return formatVNDShort(n);
}

/**
 * Hoa hồng + bảng mốc thưởng bán gói phát sinh cho nhân sự cơ sở (CEO chốt
 * 19/08/2026) KHÔNG còn hard-code ở đây. `GET /payroll` và `/payroll/me`
 * đều trả field `upsell` (`UpsellRules` — cùng object `rulesFromDB` nạp cho
 * engine tính chính khoản upsellCommission/upsellMilestoneBonus trên phiếu
 * lương), vì director có thể đổi cơ chế qua salary_rules_v2 (department
 * "_global") bất kỳ lúc nào — giữ một bản sao ở đây từng là chỗ hai số lệch
 * nhau: phiếu trả theo tỷ lệ mới, thanh tiến độ vẫn hứa tỷ lệ cũ. Các hàm
 * dưới đây thuần túy nhận `rate`/`tiers` từ response, không tự giữ số nào.
 */

/** "5%" — nhãn hiển thị từ tỷ lệ hoa hồng hiệu lực của tháng. */
export function upsellCommissionLabel(rate: number): string {
  return `${Number((rate * 100).toFixed(2))}%`;
}

/** Bảng mốc sắp TĂNG dần — thứ tự để vẽ, ngược với thứ tự API trả (giảm dần). */
export function sortUpsellTiersAsc(tiers: readonly UpsellTier[]): UpsellTier[] {
  return [...tiers].sort((a, b) => a.threshold - b.threshold);
}

/** Mốc kế tiếp chưa với tới (null = đã đạt mốc cao nhất). `tiersAsc` phải TĂNG dần. */
export function nextUpsellTier(
  total: number,
  tiersAsc: readonly UpsellTier[],
): UpsellTier | null {
  return tiersAsc.find((t) => total < t.threshold) ?? null;
}

/**
 * Mốc ĐANG hưởng — mốc cao nhất đã với tới (null = chưa tới mốc nào).
 * `tiersDesc` phải GIẢM dần (đúng thứ tự API trả về, khớp engine).
 *
 * Ngưỡng là ">=", khớp `pickUpsellTier` bên Go (upsell.go:110) và bộ ca biên
 * `TestPickUpsellTierBoundaries`: đúng 7.000.000 ĂN mốc, 6.999.999 thì chưa.
 * Viết thành ">" là cướp mốc của người vừa chạm ngưỡng đúng ngày cuối tháng.
 *
 * Màn hình dùng hàm này để tô DÒNG, không để tính TIỀN — tiền vẫn là con số
 * máy chủ gửi xuống.
 */
export function currentUpsellTier(
  total: number,
  tiersDesc: readonly UpsellTier[],
): UpsellTier | null {
  return tiersDesc.find((t) => total >= t.threshold) ?? null;
}
